import React, { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FulfillmentEditor, FLOW_LABELS } from "@/components/admin/FulfillmentEditor";

const PAID = ["paid", "completed"];
const isPaid = (o) => PAID.includes((o.payment_status || "").toLowerCase());
const provider = (o) => (o.provider || "stripe").toLowerCase();
const ref = (o) => "JO-" + String(o.id || "").slice(0, 8).toUpperCase();
const fmtDate = (ts) => (ts ? new Date(ts).toLocaleString() : "-");
const money = (cur, amt) => `${(cur || "usd").toUpperCase()} ${Number(amt || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

function Select({ value, onChange, options, testid }) {
  return (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="h-10 rounded-md border border-input bg-background px-3 text-sm">
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

function Totals({ list }) {
  const paid = list.filter(isPaid);
  const byCur = paid.reduce((acc, o) => {
    const c = (o.currency || "usd").toUpperCase();
    acc[c] = (acc[c] || 0) + Number(o.amount || 0);
    return acc;
  }, {});
  const cards = [
    ["Orders shown", list.length, "orders-total-count"],
    ["Paid", paid.length, "orders-paid-count"],
    ["Pending / failed", list.length - paid.length, "orders-unpaid-count"],
    ...Object.entries(byCur).map(([c, v]) => [`Revenue (${c})`, money(c, v), `orders-revenue-${c.toLowerCase()}`]),
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
      {cards.map(([l, v, t]) => (
        <div key={t} className="bg-white border border-brand/10 rounded-xl p-4" data-testid={t}>
          <div className="text-xs text-muted-foreground">{l}</div>
          <div className="font-heading font-bold text-xl text-brand mt-1">{v}</div>
        </div>
      ))}
    </div>
  );
}

function OrderDetail({ order, onClose, onUpdated }) {
  const rows = order && [
    ["Reference", ref(order)], ["Provider", provider(order)], ["Type", order.type === "website_build" ? "Website build" : "Shop"],
    ["Status", order.payment_status || "initiated"], ["Amount", money(order.currency, order.amount)], ["Customer email", order.email || "-"],
    ["Created", fmtDate(order.ts)], ["Updated", fmtDate(order.updated_ts)],
    ["Provider ID", order.session_id || order.paypal_order_id || order.order_tracking_id || "-"],
    ["Confirmation email", order.order_email_sent ? "Sent" : "Not sent"],
  ];
  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="order-detail-dialog">
        <DialogHeader><DialogTitle className="font-heading text-brand">Order {order && ref(order)}</DialogTitle></DialogHeader>
        {order && (
          <div className="text-sm space-y-2">
            {rows.map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4 border-b border-brand/5 pb-2">
                <span className="text-muted-foreground">{k}</span><span className="font-medium text-right break-all">{v}</span>
              </div>
            ))}
            <div className="pt-2"><div className="text-muted-foreground mb-1">Items</div>
              <ul className="list-disc pl-5">{(order.items || []).map((i) => <li key={i}>{i}</li>)}</ul>
            </div>
            {order.delivery && (
              <div className="pt-2" data-testid="order-delivery">
                <div className="text-muted-foreground mb-1">Delivery</div>
                <div className="font-medium">{order.delivery.label}{order.delivery.fee ? ` ($${order.delivery.fee})` : " (free)"}</div>
                <div>{order.delivery.full_name} · <a className="underline" href={`tel:${order.delivery.phone}`}>{order.delivery.phone}</a></div>
                {order.delivery.method !== "pickup" && (
                  <div>{[order.delivery.address, order.delivery.city, order.delivery.region, order.delivery.postal_code, order.delivery.country].filter(Boolean).join(", ")}</div>
                )}
                {order.delivery.notes && <div className="text-muted-foreground">Notes: {order.delivery.notes}</div>}
              </div>
            )}
            {order.type !== "website_build" && <FulfillmentEditor key={order.id} order={order} onUpdated={onUpdated} />}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function OrdersPanel({ orders: initial, loading }) {
  const [patched, setPatched] = useState({});
  const orders = useMemo(() => initial.map((o) => patched[o.id] || o), [initial, patched]);
  const [prov, setProv] = useState("all");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);

  const list = useMemo(() => orders.filter((o) => {
    if (prov !== "all" && provider(o) !== prov) return false;
    if (status === "paid" && !isPaid(o)) return false;
    if (status === "unpaid" && isPaid(o)) return false;
    const s = q.trim().toLowerCase();
    return !s || [ref(o), o.email, ...(o.items || [])].join(" ").toLowerCase().includes(s);
  }), [orders, prov, status, q]);

  return (
    <div data-testid="orders-panel">
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
          <Input data-testid="orders-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search reference, email or item…" className="pl-9" />
        </div>
        <Select testid="orders-filter-provider" value={prov} onChange={setProv} options={[["all", "All providers"], ["stripe", "Stripe"], ["paypal", "PayPal"], ["pesapal", "Pesapal"]]} />
        <Select testid="orders-filter-status" value={status} onChange={setStatus} options={[["all", "All statuses"], ["paid", "Paid"], ["unpaid", "Pending / failed"]]} />
      </div>
      <Totals list={list} />
      <div className="bg-white border border-brand/10 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="orders-table">
            <thead className="bg-brand-50/60 text-brand text-left">
              <tr>{["Ref", "Provider", "Items", "Amount", "Email", "Status", "Delivery", "Date"].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr>
            </thead>
            <tbody>
              {list.map((o) => (
                <tr key={o.id} onClick={() => setOpen(o)} className="border-t border-brand/5 align-top cursor-pointer hover:bg-brand-50/40 transition-colors" data-testid="order-row">
                  <td className="px-4 py-3 font-mono text-xs">{ref(o)}</td>
                  <td className="px-4 py-3 capitalize">{provider(o)}</td>
                  <td className="px-4 py-3 max-w-xs text-muted-foreground">{(o.items || []).join(", ") || "-"}</td>
                  <td className="px-4 py-3 font-medium whitespace-nowrap">{money(o.currency, o.amount)}</td>
                  <td className="px-4 py-3 text-muted-foreground">{o.email || "-"}</td>
                  <td className="px-4 py-3">
                    <Badge className={isPaid(o) ? "bg-brand-500 text-white hover:bg-brand-500" : "bg-amber-100 text-amber-700 hover:bg-amber-100"}>{o.payment_status || "initiated"}</Badge>
                  </td>
                  <td className="px-4 py-3 text-xs whitespace-nowrap" data-testid="order-fulfillment-cell">{o.fulfillment_status ? FLOW_LABELS[o.fulfillment_status] : isPaid(o) ? "New" : "-"}</td>
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{fmtDate(o.ts)}</td>
                </tr>
              ))}
              {!list.length && !loading && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground" data-testid="orders-empty">No orders match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <OrderDetail order={open} onClose={() => setOpen(null)} onUpdated={(o) => { setPatched((p) => ({ ...p, [o.id]: o })); setOpen(o); }} />
    </div>
  );
}
